/**
 * The drawn glyph set.
 *
 * Every mark in this product is drawn — `FightingFormMark`, `SelectionMark`,
 * `RopeClock`, the progress rail — and this component exists so that the last
 * few incidental marks stop being characters. A system emoji is not a glyph in
 * the design system's sense: it is painted by the platform's own colour font,
 * so it ignores `currentColor` and therefore ignores the palette, it arrives at
 * a weight nothing in the type scale chose, and it is a different drawing on
 * every operating system. A `🔒` in the Corner Note was all three at once.
 *
 * So each glyph here is a path on a 24-unit grid that paints in `currentColor`
 * and takes its size from `font-size`. It follows the palette in Day and Night,
 * it dims with the token that dims the text beside it, and it is the same shape
 * everywhere.
 *
 * Glyphs are decorative by default and hidden from assistive technology,
 * because nearly all of them sit beside the words that already say what they
 * mean. `label` is the deliberate exception, for the rare mark that is the only
 * carrier of a fact.
 */

import type { ReactNode } from "react";
import styles from "./Glyph.module.css";

export type GlyphName = "lock" | "quote";

/**
 * One drawn subpath on a `0 0 24 24` grid.
 *
 * `stroke` is per subpath rather than per glyph because a padlock genuinely
 * needs both: the shackle is a line and the body is a solid. Nothing here may
 * name a colour — the whole point of the component is that the colour arrives
 * from the surrounding text.
 */
interface GlyphPath {
  readonly d: string;
  readonly stroke?: true;
}

const GLYPHS: Readonly<Record<GlyphName, readonly GlyphPath[]>> = Object.freeze({
  /**
   * A padlock. The keyway is knocked out of the body as a second subpath under
   * `evenodd` rather than drawn on top in the canvas colour, so the mark stays
   * correct against the panel, the card and a scrim alike.
   */
  lock: [
    { d: "M8 10.5V7.5a4 4 0 0 1 8 0v3", stroke: true },
    {
      d:
        "M5 10h14a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 21H5a1.5 1.5 0 0 1-1.5-1.5v-8" +
        "A1.5 1.5 0 0 1 5 10Zm7 3.25a1.6 1.6 0 0 0-.75 3.02V18.2h1.5v-1.93A1.6 1.6 0 0 0 12 13.25Z",
    },
  ],
  /**
   * A double quotation mark, drawn rather than typed: two solids that each keep
   * the tail an open quote has, so it reads as quotation at 20 px instead of as
   * two dots.
   */
  quote: [
    { d: "M9.6 5.4 10.7 8A4.6 4.6 0 0 0 8.1 11.2h2.9v7.4H4.2v-5.9a8.6 8.6 0 0 1 5.4-7.3Z" },
    { d: "M19.4 5.4 20.5 8a4.6 4.6 0 0 0-2.6 3.2h2.9v7.4H14v-5.9a8.6 8.6 0 0 1 5.4-7.3Z" },
  ],
});

/** Every glyph the set draws, for a caller that renders a specimen of each. */
export const GLYPH_NAMES: readonly GlyphName[] = Object.freeze(
  Object.keys(GLYPHS) as GlyphName[],
);

export interface GlyphProps {
  readonly name: GlyphName;
  /**
   * The accessible name. Omitted — the normal case — the glyph is decorative
   * and hidden, because the words beside it already carry the meaning.
   */
  readonly label?: string;
  readonly className?: string;
  readonly testId?: string;
}

export function Glyph({ name, label, className, testId }: GlyphProps): ReactNode {
  const decorative = label === undefined;

  return (
    <svg
      className={className === undefined ? styles.glyph : `${styles.glyph} ${className}`}
      viewBox="0 0 24 24"
      focusable="false"
      data-ff-glyph={name}
      data-testid={testId}
      {...(decorative
        ? { "aria-hidden": true as const }
        : { role: "img" as const, "aria-label": label })}
    >
      {GLYPHS[name].map((path) =>
        path.stroke === true ? (
          <path
            key={path.d}
            d={path.d}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        ) : (
          <path key={path.d} d={path.d} fill="currentColor" fillRule="evenodd" />
        ),
      )}
    </svg>
  );
}
